import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, Bot, MessageSquare, RefreshCw, Tag } from 'lucide-react';
import PageHeader from './PageHeader';
import { API, authHeaders } from '../apiClient';

function api(path, options = {}) {
  return fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(options.headers || {})
    }
  }).then(async r => {
    if (!r.ok) throw new Error((await r.json()).detail || 'API error');
    return r.json();
  });
}

function fmtDate(value) {
  if (!value) return '—';
  try { return new Date(value).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return value; }
}

function confidenceTone(value) {
  const v = Number(value || 0);
  if (v >= 0.8) return 'success';
  if (v >= 0.6) return 'warning';
  return 'danger';
}

function cleanGuestName(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/^(booking|pulse|gość|guest)[:\s-]+/i, '')
    .trim();
}

function guestSurname(value) {
  const name = cleanGuestName(value);
  if (!name) return 'Nieznany gość';
  const parts = name.split(' ').filter(Boolean);
  return parts.length > 1 ? parts[parts.length - 1] : parts[0];
}

function conversationKey(msg) {
  const name = cleanGuestName(msg.booking_guest_name || msg.sender_name || msg.requester_name);
  const surname = guestSurname(name);
  return surname.toLocaleLowerCase('pl-PL');
}

function isOutbound(msg) {
  return ['outbound', 'outbound_auto', 'assistant', 'host'].includes(String(msg.direction || '').toLowerCase());
}

function sortByDateAsc(a, b) {
  return String(a.received_at || '').localeCompare(String(b.received_at || '')) || Number(a.id || 0) - Number(b.id || 0);
}

export default function GuestMessages() {
  const location = useLocation();
  const [data, setData] = useState({ worker: 'Paweł', categories: [], summary: [], messages: [] });
  const [accessData, setAccessData] = useState({ summary: {}, messages: [], latest_run: null, send_enabled: false, dry_run: true });
  const [workerFilter, setWorkerFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    setError('');
    try {
      const [messagesData, accessMessages] = await Promise.all([api('/messages'), api('/pulse-access-messages')]);
      setData(messagesData);
      setAccessData(accessMessages);
    }
    catch (e) { setError(e.message); }
  }

  async function runPawel() {
    setBusy(true); setError('');
    try {
      await api('/messages/process', { method: 'POST', body: JSON.stringify({ limit: 100 }) });
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function runPulseAccess() {
    setBusy(true); setError('');
    try {
      await api('/pulse-access-messages/process', { method: 'POST' });
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function dryRunAccess(id) {
    setBusy(true); setError('');
    try {
      await api(`/pulse-access-messages/${id}/dry-run`, { method: 'POST' });
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!location.hash || !(data.messages || []).length) return;
    const id = decodeURIComponent(location.hash.slice(1));
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('row-highlight');
        setTimeout(() => el.classList.remove('row-highlight'), 2500);
      }
    }, 80);
  }, [location.hash, data.messages]);

  const stats = useMemo(() => {
    const messages = data.messages || [];
    const needsReview = messages.filter(m => Number(m.needs_human_review || 0) === 1).length;
    const billing = messages.filter(m => m.category === 'billing_request').length;
    const routed = messages.filter(m => m.action_status === 'routed').length;
    return [
      { label: 'Wiadomości', value: messages.length, tone: 'info' },
      { label: 'Do oceny', value: needsReview, tone: needsReview ? 'warning' : 'success' },
      { label: 'Rachunek/faktura', value: billing, tone: billing ? 'primary' : 'info' },
      { label: 'Przekazane dalej', value: routed, tone: routed ? 'success' : 'info' },
    ];
  }, [data]);

  const categories = data.categories || [];
  const categoriesByKey = Object.fromEntries(categories.map(cat => [cat.key, cat]));
  const workerGroups = ['Mateusz', 'Maria', 'Michał'];
  const normalizeWorker = (msg) => msg.handoff_to || categoriesByKey[msg.category]?.handoff_to || 'Michał';
  const rawMessages = data.messages || [];
  const workerMessages = workerFilter === 'all' ? rawMessages : rawMessages.filter(m => normalizeWorker(m) === workerFilter);
  const messages = categoryFilter === 'all' ? workerMessages : workerMessages.filter(m => m.category === categoryFilter);
  const conversationGroups = useMemo(() => {
    const map = new Map();
    messages.forEach(msg => {
      const key = conversationKey(msg);
      const existing = map.get(key) || {
        key,
        surname: guestSurname(msg.booking_guest_name || msg.sender_name || msg.requester_name),
        names: new Set(),
        bookingRefs: new Set(),
        latestAt: '',
        messages: [],
      };
      const displayName = cleanGuestName(msg.booking_guest_name || msg.sender_name || msg.requester_name);
      if (displayName) existing.names.add(displayName);
      const bookingRef = msg.booking_external_id || msg.external_booking_id;
      if (bookingRef) existing.bookingRefs.add(bookingRef);
      if (String(msg.received_at || '') > String(existing.latestAt || '')) existing.latestAt = msg.received_at || '';
      existing.messages.push(msg);
      map.set(key, existing);
    });
    return Array.from(map.values())
      .map(group => ({
        ...group,
        names: Array.from(group.names).sort((a, b) => a.localeCompare(b, 'pl-PL')),
        bookingRefs: Array.from(group.bookingRefs).sort(),
        messages: group.messages.sort(sortByDateAsc),
      }))
      .sort((a, b) => String(b.latestAt || '').localeCompare(String(a.latestAt || '')) || a.surname.localeCompare(b.surname, 'pl-PL'));
  }, [messages]);
  const summaryByKey = Object.fromEntries((data.summary || []).map(s => [s.category, s]));
  const accessSummary = accessData.summary || {};
  const accessMessages = accessData.messages || [];
  const accessBlocked = Number(accessSummary.blocked_missing_nuki_code || 0) + Number(accessSummary.blocked_nuki_error || 0) + Number(accessSummary.blocked_missing_pulse_thread || 0) + Number(accessSummary.blocked_ambiguous_pulse_thread || 0) + Number(accessSummary.needs_review || 0) + Number(accessSummary.error || 0);
  const accessReady = Number(accessSummary.ready || 0);
  const accessSent = Number(accessSummary.sent || 0) + Number(accessSummary.sent_corrected || 0);
  const todayIso = new Date().toISOString().slice(0, 10);
  const visibleAccess = accessMessages.filter(m => {
    const date = (m.checkin || '').slice(0, 10);
    const sentAt = (m.sent_at || '').slice(0, 10);
    return date === todayIso
      || sentAt === todayIso
      || ['ready','sent','sent_corrected','blocked_missing_nuki_code','blocked_missing_pulse_thread','blocked_ambiguous_pulse_thread','blocked_nuki_error','needs_review','error'].includes(m.status);
  }).slice(0, 20);

  return (
    <div className="page-stack">
      <PageHeader
        icon={<MessageSquare />}
        title="Wiadomości gości"
        description="Panel Pawła: najpierw Dostępy Nuki i sprawy dzisiejsze, niżej klasyfikacja wiadomości przychodzących."
        helpKey="guest-messages"
        stats={stats}
        actions={<><button onClick={load}><RefreshCw size={16}/> Odśwież</button><button className="primary" disabled={busy} onClick={runPawel}><Bot size={16}/> Uruchom Pawła</button></>}
      />
      {error && <div className="alert error"><AlertTriangle size={16}/> {error}</div>}

      <div className="category-grid pulse-access-today">
        <div className="category-card access-kpi">
          <span className="category-title">Dostępy gotowe dziś</span>
          <strong>{accessReady}</strong>
          <small>można wykonać dry-run; live-send jest osobną decyzją</small>
        </div>
        <div className="category-card access-kpi">
          <span className="category-title">Zablokowane</span>
          <strong>{accessBlocked}</strong>
          <small>brak kodu Nuki, wątku Pulse albo wymaga sprawdzenia</small>
        </div>
        <div className="category-card access-kpi">
          <span className="category-title">Wysłane</span>
          <strong>{accessSent}</strong>
          <small>historia bez pokazywania pełnych kodów</small>
        </div>
        <button className="category-card active" disabled={busy} onClick={runPulseAccess}>
          <span className="category-title"><Bot size={15}/> Przygotuj Dostępy</span>
          <strong>Dry-run</strong>
          <small>tworzy/odświeża kolejkę, niczego nie wysyła</small>
        </button>
      </div>

      <div className="card table-card pulse-access-card">
        <div className="section-head">
          <div>
            <h2>Dostępy Nuki</h2>
            <p className="muted">Wiadomość „Dostępy” z indywidualnym kodem Nuki. W tabeli kod i hasło WiFi są ukryte.</p>
          </div>
          <button type="button" className="secondary" onClick={load}><RefreshCw size={16}/> Odśwież</button>
        </div>
        <div className="table-scroll">
          <table className="data-table compact-table">
            <thead><tr><th>Gość / rezerwacja</th><th>Przyjazd</th><th>Kod Nuki</th><th>Pulse</th><th>Status</th><th>Akcja</th></tr></thead>
            <tbody>
              {visibleAccess.map(msg => (
                <tr key={msg.id} id={`message-${msg.id}`}>
                  <td><strong>{msg.guest_name || 'Gość'}</strong><div className="small">{msg.external_id || msg.booking_id}</div></td>
                  <td>{msg.checkin || '—'}<div className="small">{msg.sent_at ? `wysłano: ${fmtDate(msg.sent_at)}` : `plan: ${msg.scheduled_send_at || '—'}`}</div></td>
                  <td><span className={msg.nuki_code_id ? 'pill success' : 'pill danger'}>{msg.nuki_code_id ? 'gotowy' : 'brak'}</span></td>
                  <td><span className={msg.pulse_thread_key ? 'pill success' : 'pill warning'}>{msg.pulse_thread_key ? 'wątek OK' : 'brak/niepewny'}</span></td>
                  <td><span className={`pill ${['ready','sent','sent_corrected'].includes(msg.status) ? 'success' : (msg.status || '').startsWith('blocked') ? 'danger' : 'warning'}`}>{msg.status === 'sent' ? 'wysłano' : msg.status === 'sent_corrected' ? 'wysłano korektę' : msg.status}</span><div className="small">{msg.last_error || msg.message_preview?.slice(0, 120)}</div></td>
                  <td>{['sent','sent_corrected'].includes(msg.status) ? <span className="pill success">historia</span> : <button type="button" className="secondary" disabled={busy || !msg.id} onClick={() => dryRunAccess(msg.id)}>Dry-run</button>}</td>
                </tr>
              ))}
              {!visibleAccess.length && <tr><td colSpan="6" className="empty-cell">Brak Dostępów Nuki do pokazania. Kliknij „Przygotuj Dostępy”, żeby odświeżyć kolejkę.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2>Jak pracuje Paweł</h2>
        <p className="muted">Paweł klasyfikuje wiadomości po intencji i od razu dopisuje, do kogo należy dalsza akcja. Dzięki temu Mateusz, Maria i admin dostają nie tylko etykietę, ale też konkretny pakiet: kto prosił, czego dotyczy sprawa i czego jeszcze brakuje.</p>
      </div>

      <div className="category-grid">
        <button className={workerFilter === 'all' ? 'category-card active' : 'category-card'} onClick={() => setWorkerFilter('all')}>
          <span className="category-title"><Tag size={15}/> Wszyscy workerzy</span>
          <strong>{rawMessages.length}</strong>
          <small>pełna kolejka wiadomości</small>
        </button>
        {workerGroups.map(worker => {
          const count = rawMessages.filter(m => normalizeWorker(m) === worker).length;
          const pending = rawMessages.filter(m => normalizeWorker(m) === worker && Number(m.needs_human_review || 0) === 1).length;
          const byWorker = categories.filter(cat => (cat.handoff_to || 'Michał') === worker).map(cat => cat.label).slice(0, 3).join(' · ');
          return (
            <button key={worker} className={workerFilter === worker ? 'category-card active' : 'category-card'} onClick={() => setWorkerFilter(worker)}>
              <span className="category-title"><Tag size={15}/> Do {worker}</span>
              <strong>{count}</strong>
              <small>{byWorker || 'Brak przypisanych kategorii'}</small>
              {pending > 0 && <span className="small danger-text">Do oceny: {pending}</span>}
            </button>
          );
        })}
      </div>

      <div className="card">
        <h2>Kategorie Pawła</h2>
        <p className="muted">To drugi filtr. Najpierw wybierz worker-a, potem zawęź kategorię, jeśli chcesz patrzeć tylko na jeden typ sprawy.</p>
        <div className="category-grid">
          <button className={categoryFilter === 'all' ? 'category-card active' : 'category-card'} onClick={() => setCategoryFilter('all')}>
            <span className="category-title"><Tag size={15}/> Wszystkie kategorie</span>
            <strong>{rawMessages.length}</strong>
            <small>wszystkie typy wiadomości</small>
          </button>
          {categories.map(cat => {
            const count = summaryByKey[cat.key]?.count || 0;
            const needs = summaryByKey[cat.key]?.needs_review || 0;
            return (
              <button key={cat.key} className={categoryFilter === cat.key ? 'category-card active' : 'category-card'} onClick={() => setCategoryFilter(cat.key)}>
                <span className="category-title"><Tag size={15}/> {cat.label}</span>
                <strong>{count}</strong>
                <small>{cat.description}</small>
                <small><strong>Do:</strong> {cat.handoff_to || '—'}</small>
                {Array.isArray(cat.required_fields) && cat.required_fields.length > 0 && (
                  <small><strong>Potrzebne:</strong> {cat.required_fields.join(', ')}</small>
                )}
                {needs > 0 && <span className="small danger-text">Do oceny: {needs}</span>}
              </button>
            );
          })}
        </div>
      </div>


      <div className="card guest-conversations-card">
        <div className="section-head">
          <div>
            <h2>Wątki według nazwisk gości</h2>
            <p className="muted">Wiadomości są pogrupowane po nazwisku/nazwie gościa i pokazane chronologicznie, żeby było widać ciąg wymiany.</p>
          </div>
          <span className="pill info">{conversationGroups.length} wątków</span>
        </div>

        <div className="guest-thread-list">
          {conversationGroups.map(group => (
            <section className="guest-thread" key={group.key}>
              <div className="guest-thread-header">
                <div>
                  <h3>{group.surname}</h3>
                  <div className="small">{group.names.join(' · ') || 'Nieznany gość'}</div>
                  {group.bookingRefs.length > 0 && <div className="small">Rezerwacje: {group.bookingRefs.join(', ')}</div>}
                </div>
                <div className="guest-thread-meta">
                  <strong>{group.messages.length}</strong>
                  <span>wiad.</span>
                  <small>ostatnia: {fmtDate(group.latestAt)}</small>
                </div>
              </div>

              <div className="guest-thread-timeline">
                {group.messages.map(msg => {
                  const outbound = isOutbound(msg);
                  const bookingRef = msg.booking_external_id || msg.external_booking_id;
                  return (
                    <article key={msg.id} id={`message-${msg.id}`} className={outbound ? 'message-bubble outbound' : 'message-bubble inbound'}>
                      <div className="message-bubble-head">
                        <strong>{outbound ? 'Odpowiedź / automat' : (msg.sender_name || 'Gość')}</strong>
                        <span>{fmtDate(msg.received_at)} · {msg.channel || '—'} · {msg.direction || '—'}</span>
                      </div>
                      <div className="message-bubble-body">{msg.body}</div>
                      <div className="message-bubble-foot">
                        <span className={`pill ${confidenceTone(msg.classification_confidence)}`}>{msg.category_label || 'Niepewne'}</span>
                        <span className="small">Paweł: {msg.action_status || msg.status || '—'} · Do: {msg.handoff_to || categoriesByKey[msg.category]?.handoff_to || '—'}</span>
                        {bookingRef && <span className="small">Rezerwacja: {bookingRef}</span>}
                        {Number(msg.needs_human_review || 0) === 1 && <span className="small danger-text">Wymaga oceny</span>}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
          {!conversationGroups.length && <div className="empty-cell">Brak wiadomości w tej kategorii.</div>}
        </div>
      </div>
    </div>
  );
}
